import { jsPDF } from "jspdf";

/* ---------------------------------------------------
   CLIPDROP AI IMAGE ENHANCEMENT
   Docs: https://clipdrop.co/apis/docs/remove-background
   Free: 100 credits/day — get key at clipdrop.co/apis
--------------------------------------------------- */

const CLIPDROP_API_KEY = process.env.REACT_APP_CLIPDROP_API_KEY || "";

/**
 * Removes the background from an image file using Clipdrop API.
 * Returns a new File with transparent/white background.
 * @param {File} file - original image file
 * @param {string} [replaceBgColor="#ffffff"] - color to fill behind subject (null = keep transparent PNG)
 * @returns {Promise<File>}
 */
export async function removeBackground(file, replaceBgColor = "#ffffff") {
    if (!CLIPDROP_API_KEY) {
        throw new Error("Clipdrop API key not set. Add REACT_APP_CLIPDROP_API_KEY to your .env file.");
    }

    const formData = new FormData();
    formData.append("image_file", file);

    const response = await fetch("https://clipdrop-api.co/remove-background/v1", {
        method: "POST",
        headers: { "x-api-key": CLIPDROP_API_KEY },
        body: formData,
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(`Clipdrop error ${response.status}: ${err.error || response.statusText}`);
    }

    const blob = await response.blob(); // PNG with transparent bg

    // If a background color is requested, composite onto a colored canvas
    if (replaceBgColor) {
        const coloredBlob = await flattenOnColor(blob, replaceBgColor);
        return new File([coloredBlob], file.name, { type: "image/jpeg" });
    }

    return new File([blob], file.name.replace(/\.\w+$/, ".png"), { type: "image/png" });
}

/**
 * Composites a transparent PNG blob onto a solid color background.
 * @param {Blob} pngBlob
 * @param {string} color - CSS color string
 * @returns {Promise<Blob>}
 */
function flattenOnColor(pngBlob, color) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(pngBlob);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob(resolve, "image/jpeg", 0.95);
        };
        img.src = url;
    });
}

/**
 * Entry point
 */
export async function generateImagePDF({ files, count = 1, size = "passport", bgColor = "#ffffff", enhance = false }) {
    count = count >= 28 ? 28 : count;

    if (!files || files.length === 0) {
        throw new Error("No files provided");
    }

    // Optionally run AI background removal on all files before generating PDF
    if (enhance) {
        files = await Promise.all(
            files.map(f => removeBackground(f, bgColor).catch(err => {
                console.warn(`Background removal failed for ${f.name}:`, err.message);
                return f; // fall back to original on error
            }))
        );
    }

    if (size === "3R") return generateFixedSize(files, "3R", count = 1);
    if (size === "4R") return generateFixedSize(files, "4R", count = 1);
    if (size === "6R") return generateFixedSize(files, "6R", count = 1);
    if (size === "landscape") return generateLandscape(files, count);
    if (size === "ngo_combined") return generateNGOCombinedPDF(files, count, bgColor);
    if (size === "nid_side_by_side") return generateNIDSideBySidePDF(files, count, bgColor);

    return generatePortrait(files, count, size);
}

/* ---------------------------------------------------
   CONFIG
--------------------------------------------------- */

function getPortraitConfig(size) {
    const map = {
        passport: { w: 38, h: 50, margin: 3 },
        stamp: { w: 20, h: 25, margin: 3 },
        "2x4": { w: 50, h: 76, margin: 1.5 },
        "2x2": { w: 50, h: 50, margin: 1 },
        nid: { w: 85.55, h: 54.31, margin: 3, x: 62.2, y: 50.8, single: true },
        nid_side_by_side: { w: 85.55, h: 54.31, margin: 3, x: 62.2, y: 50.8, single: true },
        nid_combined: { w: 90, h: 115, margin: 3, x: 60, y: 50.8, single: true },
        office_id_card: { w: 54, h: 85.6, margin: 3, x: 54, y: 50.8, single: true },
        bordered: { w: 184.6, h: 271.6, margin: 3, x: 12.7, y: 12.7, single: true },
        mrp: { w: 125, h: 176, margin: 3, x: 42.5, y: 50.8, single: true },
    };

    return map[size] || map.passport;
}

/* ---------------------------------------------------
   PORTRAIT PDF
--------------------------------------------------- */

async function generatePortrait(files, count, size) {
    const pdf = new jsPDF("p", "mm", "a4");
    const cfg = getPortraitConfig(size);

    let x = cfg.x ?? 3;
    let y = cfg.y ?? 3;
    const startX = x;
    const startY = y;

    const maxW = pdf.internal.pageSize.width;
    const maxH = pdf.internal.pageSize.height;

    for (const file of files) {
        const img = await loadImage(file);

        for (let i = 0; i < count; i++) {
            pdf.addImage(img, "JPEG", x, y, cfg.w, cfg.h);

            if (!cfg.single) {
                pdf.rect(x, y, cfg.w, cfg.h);
            }

            x += cfg.w + cfg.margin;

            if (x + cfg.w > maxW) {
                x = startX;
                y += cfg.h + cfg.margin;
            }

            if (y + cfg.h > maxH) {
                pdf.addPage();
                x = startX;
                y = startY;
            }

            if (cfg.single) break;
        }
    }

    pdf.save("images.pdf");
}

/* ---------------------------------------------------
   LANDSCAPE PDF
--------------------------------------------------- */

async function generateLandscape(files, count) {
    const pdf = new jsPDF("l", "mm", "a4");

    const imgW = 38;
    const imgH = 49;
    const margin = 2.5;
    const marginX = 3.5;

    let x = 3;
    let y = 3;

    const maxW = pdf.internal.pageSize.width;
    const maxH = pdf.internal.pageSize.height;

    for (const file of files) {
        const img = await loadImage(file);

        for (let i = 0; i < count; i++) {
            pdf.addImage(img, "JPEG", x, y, imgW, imgH);
            pdf.rect(x, y, imgW, imgH);

            y += imgH + margin;

            if (y + imgH > maxH) {
                x += imgW + marginX;
                y = 3;

                if (x + imgW > maxW) {
                    pdf.addPage();
                    x = 3;
                    y = 3;
                }
            }
        }
    }

    pdf.save("images.pdf");
}

/* ---------------------------------------------------
   FIXED PHOTO SIZES (3R, 4R, 6R)
--------------------------------------------------- */

const FIXED_SIZES = {
    "3R": { w: 89, h: 127, margin: 6 },
    "4R": { w: 99, h: 140, margin: 4 },
    "6R": { w: 202, h: 142, margin: 6 },
};

async function generateFixedSize(files, type, count) {
    const pdf = new jsPDF("p", "mm", "a4");
    const cfg = FIXED_SIZES[type];

    let x = 3;
    let y = 3;

    const maxW = pdf.internal.pageSize.width;
    const maxH = pdf.internal.pageSize.height;

    for (const file of files) {
        const img = await loadImage(file);

        pdf.addImage(img, "JPEG", x, y, cfg.w, cfg.h);

        x += cfg.w + cfg.margin;

        if (x + cfg.w > maxW) {
            x = 3;
            y += cfg.h + cfg.margin;
        }

        if (y + cfg.h > maxH) {
            pdf.addPage();
            x = 3;
            y = 3;
        }
    }

    pdf.save("images.pdf");
}

/* ---------------------------------------------------
   HELPERS
--------------------------------------------------- */

function loadImage(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = e => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}


async function combineTwoImagesSideBySide(
    img1,
    img2,
    {
        width = 760,
        height = 490,
        gap = 0
    } = {},
    bgColor
) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    const halfWidth = (width - gap) / 2;

    // left image — clipped to its cell
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, halfWidth, height);
    ctx.clip();
    drawCover(ctx, img1, 0, 0, halfWidth, height, "center");
    ctx.restore();

    // right image — clipped to its cell
    ctx.save();
    ctx.beginPath();
    ctx.rect(halfWidth + gap, 0, halfWidth, height);
    ctx.clip();
    drawCover(ctx, img2, halfWidth + gap, 0, halfWidth, height, "center");
    ctx.restore();

    return canvas.toDataURL("image/jpeg", 1.0);
}

function drawCover(ctx, img, x, y, w, h, cropFrom = "center") {
    const imgRatio = img.width / img.height;
    const boxRatio = w / h;

    let drawWidth, drawHeight;

    if (imgRatio > boxRatio) {
        drawHeight = h;
        drawWidth = h * imgRatio;
    } else {
        drawWidth = w;
        drawHeight = w / imgRatio;
    }

    let offsetX = x;
    let offsetY = y + (h - drawHeight) / 2;

    if (cropFrom === "center") {
        offsetX = x + (w - drawWidth) / 2;
    } else if (cropFrom === "right") {
        offsetX = x + (w - drawWidth);
    } else if (cropFrom === "left") {
        offsetX = x;
    }

    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
}


/* ---------------------------------------------------
   Generate NGO Combined PDF
--------------------------------------------------- */

function base64ToFile(base64, filename = "combined.jpg") {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
}

function rotateBase64Image90CounterClockwise(base64) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // Swap width and height
            canvas.width = img.height;
            canvas.height = img.width;

            // Rotate 90° counterclockwise
            ctx.translate(0, canvas.height);
            ctx.rotate((-90 * Math.PI) / 180);

            // Draw original image
            ctx.drawImage(img, 0, 0);

            resolve(canvas.toDataURL("image/jpeg", 1.0));
        };
        img.src = base64;
    });
}

async function prepareNGOCombinedFiles(files, bgColor) {
    const combinedFiles = [];
    console.log('bgColor: ', bgColor);
    for (let i = 0; i < files.length; i += 2) {
        const img1 = await loadImage(files[i]);
        const img2 = files[i + 1]
            ? await loadImage(files[i + 1])
            : null;

        let combinedBase64;

        if (img2) {
            combinedBase64 = await combineTwoImagesSideBySide(
                img1,
                img2,
                { width: 490, height: 380 },
                bgColor
            );
        } else {
            const canvas = document.createElement("canvas");
            canvas.width = img1.width;
            canvas.height = img1.height;
            
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = bgColor || "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img1, 0, 0);

            combinedBase64 = canvas.toDataURL("image/jpeg", 1.0);
        }

        // // Rotate 90 degrees
        // combinedBase64 = await rotateBase64Image90(combinedBase64);
        // Rotate 90 degrees counterclockwisw
        combinedBase64 = await rotateBase64Image90CounterClockwise(combinedBase64);

        combinedFiles.push(
            base64ToFile(combinedBase64, `ngo_combined_${i}.jpg`)
        );
    }

    return combinedFiles;
}

async function generateNGOCombinedPDF(files, count, bgColor) {

    const combinedFiles = await prepareNGOCombinedFiles(files, bgColor);
    generateLandscape(
        combinedFiles,
        4, 
        "landscape"
    );
}

/* ---------------------------------------------------
   NID Side-by-Side PDF
   Takes 2 files (front + back) and places them side
   by side — each card 3.368 × 2.138 in (85.55 × 54.31 mm).
   Repeats `count` times per pair across the A4 page.
--------------------------------------------------- */

async function generateNIDSideBySidePDF(pairs, _count, bgColor = "#ffffff") {
    const pdf = new jsPDF("p", "mm", "a4");

    // Each NID image gets its own full card slot — 3.368 × 2.138 in
    const cardW   = 85.55;  // 3.368in × 25.4
    const cardH   = 54.31;  // 2.138in × 25.4
    const gap     = 3;       // gap between front and back card within a pair
    const rowGap  = 5;       // vertical gap between rows
    const pairGap = gap * 2; // horizontal gap after a pair before the next

    const pageW = pdf.internal.pageSize.width;   // 210mm
    const pageH = pdf.internal.pageSize.height;  // 297mm

    const pairW   = cardW + gap + cardW;                    // 174.2mm
    const topMargin = 12.7;                                 // 0.5 inch
    const startX  = (pageW - pairW) / 2;                   // centered horizontally
    const startY  = topMargin;

    let x = startX;
    let y = startY;

    // Each element in `pairs` is { front: File, back: File, count: number }
    for (const pair of pairs) {
        const img1 = await loadImage(pair.front);
        const img2 = await loadImage(pair.back);

        const base64_1 = imageToBase64(img1, cardW, cardH, bgColor);
        const base64_2 = imageToBase64(img2, cardW, cardH, bgColor);

        const pairCount = pair.count ?? 1;

        for (let j = 0; j < pairCount; j++) {
            // Wrap to next row if pair doesn't fit
            if (x + pairW > pageW) {
                x = startX;
                y += cardH + rowGap;
            }

            // New page if row doesn't fit
            if (y + cardH > pageH) {
                pdf.addPage();
                x = startX;
                y = startY;
            }

            // Place front card
            pdf.addImage(base64_1, "JPEG", x, y, cardW, cardH);
            // Place back card after gap
            pdf.addImage(base64_2, "JPEG", x + cardW + gap, y, cardW, cardH);

            // Advance by pair width + 2× gap
            x += pairW + pairGap;
        }
    }

    pdf.save("images.pdf");
}

/** Renders a single image onto a canvas (cover-fit + clip) and returns base64.
 *  px/py are in mm — we use a fixed pixel scale for quality. */
function imageToBase64(img, cardWmm, cardHmm, bgColor = "#ffffff") {
    // Use native image resolution — no downscale, no quality loss
    const cw = img.width;
    const ch = img.height;

    const canvas = document.createElement("canvas");
    canvas.width  = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");

    // Background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, cw, ch);

    // Draw image at full native size — already fits perfectly
    ctx.drawImage(img, 0, 0, cw, ch);

    return canvas.toDataURL("image/jpeg", 1.0);
}
